/**
 * URL segments for the resto-app-admin shell vs embedded stock microfrontends.
 *
 * `patchHistoryForAdminShell` only wraps the **host** React Router v5 `history` from `src/index.js`.
 * It does not load or modify the stock-managment-app bundle: that app mounts in a separate React tree
 * (`operations-stock-app`) with its own RR6 `RouterProvider` and syncs via `window.history` under
 * `/operations/...`. Those URLs must stay unprefixed — handled by `shouldSkipAdminShellPrefix` for
 * `ROUTE_PREFIX.OPERATIONS`. Admin stock (`admin-stock-app` / adm-stock-management) lives under
 * `/admin/...`, which is also skipped so paths are not doubled.
 */
export const ROUTE_PREFIX = {
  ADMIN: '/admin',
  OPERATIONS: '/operations',
}

/** Join a shell prefix with an app-internal route (e.g. `/dashboard` → `/admin/dashboard`). */
export function joinRoutePrefix(prefix, route) {
  if (route == null || route === '') return prefix || ''
  const p = (prefix || '').replace(/\/$/, '')
  const r = route.startsWith('/') ? route : `/${route}`
  return `${p}${r}`
}

/**
 * The link for a route template from the API: `/messaging/:ruuid/:bruuid/:chatId` → `/messaging`.
 * Its route is registered with `withOptionalParams`, so the bare path still opens the page.
 */
export function routeLinkPath(route) {
  if (!route) return route
  const i = route.indexOf('/:')
  return i === -1 ? route : route.slice(0, i) || '/'
}

/** `/messaging/:ruuid/:chatId` → `/messaging/:ruuid?/:chatId?` */
export function withOptionalParams(route) {
  return route ? route.replace(/\/:(\w+)(?![\w?])/g, '/:$1?') : route
}

/** After login / default landing for the main admin shell */
export const DEFAULT_POST_LOGIN_PATH = joinRoutePrefix(ROUTE_PREFIX.ADMIN, '/dashboard')

/**
 * Recursively prefix `route` on permission nodes from the API so links match the shell URL.
 */
export function prefixPermissionTree(nodes, prefix) {
  if (!nodes?.length) return nodes
  return nodes.map((node) => {
    const n = { ...node }
    if (n.route) n.route = joinRoutePrefix(prefix, n.route)
    if (n.subPage?.length) n.subPage = prefixPermissionTree(n.subPage, prefix)
    return n
  })
}

/** Strip `/admin` or `/operations` so permission routes (`/dashboard`, …) match `location.pathname`. */
export function stripShellPrefix(pathname) {
  if (!pathname) return pathname
  for (const p of [ROUTE_PREFIX.ADMIN, ROUTE_PREFIX.OPERATIONS]) {
    if (pathname === p || pathname === `${p}/`) return '/'
    if (pathname.startsWith(`${p}/`)) return pathname.slice(p.length)
  }
  return pathname
}

function hasShellPrefix(pathname, prefix) {
  if (!pathname) return false
  return pathname === prefix || pathname.startsWith(`${prefix}/`)
}

/** Routes that must not be prefixed (auth, errors, shell roots). */
const SHELL_PUBLIC_PREFIXES = ['/', '/login', '/not-found', '/forbidden']

/** Normalize pathname so `operations/...` is treated as `/operations/...`. */
function normalizePathnameForShell(pathname) {
  if (pathname == null || pathname === '') return pathname
  if (pathname === '/') return '/'
  return pathname.startsWith('/') ? pathname : `/${pathname}`
}

/**
 * True if this pathname already belongs to a shell area or is a public top-level route.
 */
export function shouldSkipAdminShellPrefix(pathname) {
  if (pathname == null || pathname === '') return true
  const normalized = normalizePathnameForShell(pathname)
  if (hasShellPrefix(normalized, ROUTE_PREFIX.ADMIN)) return true
  if (hasShellPrefix(normalized, ROUTE_PREFIX.OPERATIONS)) return true
  for (const p of SHELL_PUBLIC_PREFIXES) {
    if (normalized === p || normalized.startsWith(`${p}/`)) return true
  }
  return false
}

/**
 * Ensure in-app navigations use the admin shell when missing `/admin` or `/operations`.
 * Leaves full URLs, `/login`, `/not-found`, `/forbidden`, `/operations/...`, and `/admin/...` unchanged.
 *
 * @param {string} path - Full path string (may include `?query` / `#hash`)
 * @returns {string}
 */
export function ensureAdminShellPath(path) {
  if (path == null || path === '') return path
  const full = String(path)
  if (full.startsWith('http://') || full.startsWith('https://') || full.startsWith('//')) return full

  let pathname = full
  let rest = ''
  const q = full.indexOf('?')
  const h = full.indexOf('#')
  if (q >= 0) {
    pathname = full.slice(0, q)
    rest = full.slice(q)
  } else if (h >= 0) {
    pathname = full.slice(0, h)
    rest = full.slice(h)
  }

  const normalized = normalizePathnameForShell(pathname)

  if (shouldSkipAdminShellPrefix(normalized)) {
    return `${normalized}${rest}`
  }

  return `${joinRoutePrefix(ROUTE_PREFIX.ADMIN, normalized)}${rest}`
}

/**
 * Normalize `history.push` / `history.replace` argument (string or location object).
 * @param {string | { pathname?: string, search?: string, hash?: string, state?: unknown }} to
 */
export function normalizeHistoryNavigationArg(to) {
  if (typeof to === 'number') return to
  if (typeof to === 'string') return ensureAdminShellPath(to)
  if (to && typeof to === 'object' && typeof to.pathname === 'string') {
    return { ...to, pathname: ensureAdminShellPath(to.pathname) }
  }
  return to
}

/**
 * Wrap `history.push` and `history.replace` so relative shell paths get `/admin` (global, one place).
 * @param {{ push: Function, replace: Function }} history - `createBrowserHistory()` instance
 */
export function patchHistoryForAdminShell(history) {
  const push = history.push.bind(history)
  const replace = history.replace.bind(history)
  history.push = (to, state) => push(normalizeHistoryNavigationArg(to), state)
  history.replace = (to, state) => replace(normalizeHistoryNavigationArg(to), state)
}
